import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import { Loader } from "@themes/pollinator/ui/Loader";
import { usePlatformStore } from "./store";
import { makeRoute } from "@core/utils/makeRoute";
import NotFoundPage from "@themes/pollinator/pages/NotFoundPage";
import ErrorPage from "@themes/pollinator/pages/ErrorPage";

// Wrapper component to protect routes that require authentication
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const user = usePlatformStore((s) => s.user);
  const isHydrated = usePlatformStore((s) => s.isHydrated);
  const location = useLocation();

  // Wait for store hydration from localStorage
  if (!isHydrated) {
    return <Loader isFullPage />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to={`/login?redirect=${location.pathname}`} replace />;
  }

  return <>{children}</>;
}

// Guard component that redirects when support page is disabled
function SupportPageGuard({ children }: { children: React.ReactNode }) {
  const user = usePlatformStore((s) => s.user);
  const isHydrated = usePlatformStore((s) => s.isHydrated);
  const isSupportPageDisabled = usePlatformStore(
    (s) => s.featureFlags.isSupportPageDisabled
  );

  // Wait for store hydration from localStorage
  if (!isHydrated) {
    return <Loader isFullPage />;
  }

  // Redirect when support page is disabled
  if (isSupportPageDisabled) {
    // Logged in users go to dashboard, others go to home
    return <Navigate to={user ? "/dashboard" : "/"} replace />;
  }

  return <>{children}</>;
}

// Bind makeRoute with the app's error page
const route = (
  path: string,
  element: React.ReactNode,
  layout: React.ReactNode
) => makeRoute(path, element, layout, <ErrorPage />);

// Helper for routes that require authentication
const protectedRoute = (
  path: string,
  element: React.ReactNode,
  layout: React.ReactNode
) =>
  makeRoute(
    path,
    <ProtectedRoute>{element}</ProtectedRoute>,
    layout,
    <ErrorPage />
  );

// Pages - imported from apps/platform/pages/
import DashboardPage from "./pages/DashboardPage";
import DashboardProgramPage from "./pages/DashboardProgramPage";
import DashboardSettingsPage from "./pages/DashboardSettingsPage";
import DashboardSupportHomePage from "./pages/DashboardSupportHomePage";
import DashboardSupportArticlePage from "./pages/DashboardSupportArticlePage";
import LoginPage from "./pages/LoginPage";
import LogoutPage from "./pages/LogoutPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import InvitationJoinPage from "./pages/InvitationJoinPage";
import InvitationFitQuizPage from "./pages/InvitationFitQuizPage";
import InvitationSignupPage from "./pages/InvitationSignupPage";
import InvitationsPage from "./pages/InvitationsPage";
import MarketingHome from "./pages/MarketingHome";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import ProgramPage from "./pages/ProgramPage";
import ResourcesPage from "./pages/ResourcesPage";
import ProfilePage from "./pages/ProfilePage";
import ParticipantOnboardingPage from "./pages/ParticipantOnboardingPage";
import OrgAdminOnboardingPage from "./pages/OrgAdminOnboardingPage";
import OrganizationAdminPage from "./pages/OrganizationAdminPage";

// Video pages - still in features (excluded from refactor)
import VideoJoinPage from "./features/video/pages/VideoJoinPage";
import VideoMeetingPage from "./features/video/pages/VideoMeetingPage";
import VideoEndedPage from "./features/video/pages/VideoEndedPage";

// Layouts
import DashboardLayout from "./layouts/DashboardLayout";
import MarketingLayout from "./layouts/MarketingLayout";
import InvitationLayout from "./layouts/InvitationLayout";
import OnboardingLayout from "./layouts/OnboardingLayout";
import VideoLayout from "./layouts/VideoLayout";
import VideoEndLayout from "./layouts/VideoEndLayout";
import StaticPageLayout from "./layouts/StaticPageLayout";
import SupportLayout from "./layouts/SupportLayout";
import AuthLayout from "./layouts/AuthLayout";

// Static page container
import { StaticPageContainer } from "./features/staticPage";

// Onboarding gate
import { OnboardingGate } from "./features/onboarding";

export const router = createBrowserRouter([
  // Marketing
  route("/", <MarketingHome />, <MarketingLayout title="Home" />),
  route(
    "/programs",
    <Navigate to="/#program" replace />,
    <MarketingLayout title="Programs" />
  ),
  route(
    "/how-it-works",
    <Navigate to="/#how-it-works" replace />,
    <MarketingLayout title="How It Works" />
  ),
  route(
    "/team",
    <Navigate to="/#team" replace />,
    <MarketingLayout title="Team" />
  ),
  route(
    "/donate",
    <Navigate to="/#donate" replace />,
    <MarketingLayout title="Donate" />
  ),

  // Invitations
  route(
    "/invitation/:shortcode",
    <MarketingHome />,
    <MarketingLayout title="You're Invited" />
  ),
  route(
    "/invitation/:shortcode/join",
    <InvitationJoinPage />,
    <InvitationLayout title="Join Program" />
  ),
  route(
    "/invitation/:shortcode/fit",
    <InvitationFitQuizPage />,
    <InvitationLayout title="Fit Quiz" />
  ),
  route(
    "/invitation/:shortcode/signup",
    <InvitationSignupPage />,
    <InvitationLayout title="Sign Up" />
  ),

  // Auth
  route("/login", <LoginPage />, <AuthLayout title="Login" />),
  route("/logout", <LogoutPage />, <AuthLayout title="Logout" />),
  route(
    "/forgot-password",
    <ForgotPasswordPage />,
    <AuthLayout title="Forgot Password" />
  ),
  route(
    "/reset-password",
    <ResetPasswordPage />,
    <AuthLayout title="Reset Password" />
  ),

  // Dashboard
  protectedRoute(
    "/dashboard",
    <DashboardPage />,
    <DashboardLayout title="Dashboard" />
  ),
  protectedRoute(
    "/dashboard/:id",
    <OnboardingGate><DashboardProgramPage /></OnboardingGate>,
    <DashboardLayout title="Dashboard" />
  ),
  protectedRoute(
    "/account",
    <DashboardSettingsPage />,
    <DashboardLayout title="Your Account" />
  ),
  // Redirect old /notifications URL to dashboard
  protectedRoute(
    "/notifications",
    <Navigate to="/dashboard" replace />,
    <DashboardLayout title="Dashboard" />
  ),
  protectedRoute(
    "/program/:id/invitations",
    <InvitationsPage />,
    <DashboardLayout title="Invitations" />
  ),
  protectedRoute(
    "/program/:id",
    <ProgramPage />,
    <DashboardLayout title="Program" />
  ),
  protectedRoute(
    "/program/:id/:tab",
    <ProgramPage />,
    <DashboardLayout title="Program" />
  ),
  protectedRoute(
    "/program/:id/resources",
    <ResourcesPage />,
    <DashboardLayout title="Resources" />
  ),
  protectedRoute(
    "/program/:id/profile",
    <ProfilePage />,
    <DashboardLayout title="Profile" />
  ),
  protectedRoute(
    "/program/:id/profile/:tab",
    <ProfilePage />,
    <DashboardLayout title="Profile" />
  ),
  // Participant onboarding
  protectedRoute(
    "/program/:id/onboarding/participant",
    <ParticipantOnboardingPage />,
    <OnboardingLayout title="Onboarding" />
  ),
  // Org admin onboarding
  protectedRoute(
    "/program/:id/organization/:orgId/onboarding",
    <OrgAdminOnboardingPage />,
    <OnboardingLayout title="Onboarding" />
  ),

  // Organization Admin
  protectedRoute(
    "/program/:programId/organization/:orgId/admin",
    <OnboardingGate><OrganizationAdminPage /></OnboardingGate>,
    <DashboardLayout title="Organization Admin" />
  ),
  protectedRoute(
    "/program/:programId/organization/:orgId/admin/:tab",
    <OnboardingGate><OrganizationAdminPage /></OnboardingGate>,
    <DashboardLayout title="Organization Admin" />
  ),

  // Support
  route(
    "/support",
    <SupportPageGuard><DashboardSupportHomePage /></SupportPageGuard>,
    <SupportLayout title="Support" />
  ),
  route(
    "/support/:articleId",
    <SupportPageGuard><DashboardSupportArticlePage /></SupportPageGuard>,
    <SupportLayout title="Support" />
  ),

  // Video
  route(
    "/video/join/:meetingId",
    <Navigate to="/video/join" replace />,
    <VideoLayout isWithNavigation />
  ),
  protectedRoute(
    "/video/join",
    <VideoJoinPage />,
    <VideoLayout isWithNavigation />
  ),
  protectedRoute(
    "/video/meeting/:meetingId",
    <VideoMeetingPage />,
    <VideoLayout />
  ),
  protectedRoute(
    "/video/end/:meetingId",
    <VideoEndedPage />,
    <VideoEndLayout isWithNavigation />
  ),

  // Not Found
  route("/not-found", <NotFoundPage />, <StaticPageLayout title="Not Found" />),

  // Static Pages
  route("/about", <AboutPage />, <SupportLayout title="About" />),
  route("/contact", <ContactPage />, <SupportLayout title="Contact" />),

  route(
    "/terms",
    <Navigate to="/terms-of-service" replace />,
    <StaticPageLayout title="Terms of Service" />
  ),
  route("/:slug", <StaticPageContainer />, <StaticPageLayout title="" />),
]);
